/**
 * BusinessDomain·DomainCategory 타입 및 순수 함수 (Frontend·Backend 공유)
 * 단일 소스: src/shared/types/business-domain.ts
 *
 * Boundary SSOT (overlap rules, SSOT per fact): docs/product/architecture/BUSINESS_SUBDOMAIN_BOUNDARY_CANON.md
 *
 * DomainCategory = 비즈니스 CRUD·Entity Auth 권한 (11 cat).
 * conscious/neuro 등 engines cat은 MatrixCategory — @shared/types/matrix-domain
 */

export type BusinessDomain =
  | 'internal_entity'
  | 'external_entity'
  | 'user_entity'
  | 'sellable_item'
  | 'service_item'
  | 'asset_item'
  | 'consumable_item'
  | 'raw_material_item'
  | 'finance_state'
  | 'finance_flow'
  | 'finance_budget'
  | 'legal_entity'
  | 'currency_rate'
  | 'intercompany'
  | 'physical_space'
  | 'virtual_space'
  | 'knowledge_report'
  | 'knowledge_policy'
  | 'knowledge_plan'
  | 'semantic_formation'
  | 'ephemeral_formation'
  | 'recurrent_performance'
  | 'ephemeral_performance'
  | 'calendar_anchor'
  | 'calendar_trigger'
  | 'calendar_cycle'
  | 'flow_definition'
  | 'stream_definition'
  | 'action_binding'
  | 'flow_execution'
  | 'stream_execution'
  | 'flow_event'
  | 'stream_event'
  | 'manufacturing_bom'
  | 'production_order'
  | 'quality_inspection'
  | 'warehouse_picking_list'
  | 'inventory_shortage'
  | 'organizational_state';

export type DomainCategory =
  | 'entity'
  | 'item'
  | 'finance'
  | 'space'
  | 'knowledge'
  | 'appeal'
  | 'calendar'
  | 'event'
  | 'manufacturing'
  | 'finance_advanced'
  | 'organizational';

const DOMAIN_MAP: Record<BusinessDomain, DomainCategory> = {
  internal_entity: 'entity',
  external_entity: 'entity',
  user_entity: 'entity',
  sellable_item: 'item',
  service_item: 'item',
  asset_item: 'item',
  consumable_item: 'item',
  raw_material_item: 'item',
  finance_state: 'finance',
  finance_flow: 'finance',
  finance_budget: 'finance',
  legal_entity: 'finance_advanced',
  currency_rate: 'finance_advanced',
  intercompany: 'finance_advanced',
  physical_space: 'space',
  virtual_space: 'space',
  knowledge_report: 'knowledge',
  knowledge_policy: 'knowledge',
  knowledge_plan: 'knowledge',
  semantic_formation: 'appeal',
  ephemeral_formation: 'appeal',
  recurrent_performance: 'appeal',
  ephemeral_performance: 'appeal',
  calendar_anchor: 'calendar',
  calendar_trigger: 'calendar',
  calendar_cycle: 'calendar',
  flow_definition: 'event',
  stream_definition: 'event',
  action_binding: 'event',
  flow_execution: 'event',
  stream_execution: 'event',
  flow_event: 'event',
  stream_event: 'event',
  manufacturing_bom: 'manufacturing',
  production_order: 'manufacturing',
  quality_inspection: 'manufacturing',
  warehouse_picking_list: 'manufacturing',
  inventory_shortage: 'item',
  organizational_state: 'organizational',
};

/** Matrix legacy subdomains — read/migrate only until removal from VALID_DOMAINS */
export const LEGACY_BUSINESS_DOMAINS: ReadonlySet<BusinessDomain> = new Set([
  'stream_definition',
  'stream_execution',
  'stream_event',
]);

export function isLegacyBusinessDomain(value: BusinessDomain): boolean {
  return LEGACY_BUSINESS_DOMAINS.has(value);
}

const CATEGORY_MAP: Record<DomainCategory, BusinessDomain[]> = {
  entity: ['internal_entity', 'external_entity', 'user_entity'],
  item: [
    'sellable_item',
    'service_item',
    'asset_item',
    'consumable_item',
    'raw_material_item',
    'inventory_shortage',
  ],
  finance: ['finance_state', 'finance_flow', 'finance_budget'],
  finance_advanced: ['legal_entity', 'currency_rate', 'intercompany'],
  space: ['physical_space', 'virtual_space'],
  knowledge: ['knowledge_report', 'knowledge_policy', 'knowledge_plan'],
  appeal: [
    'semantic_formation',
    'ephemeral_formation',
    'recurrent_performance',
    'ephemeral_performance',
  ],
  calendar: ['calendar_anchor', 'calendar_trigger', 'calendar_cycle'],
  event: [
    'flow_definition',
    'stream_definition',
    'action_binding',
    'flow_execution',
    'stream_execution',
    'flow_event',
    'stream_event',
  ],
  manufacturing: [
    'manufacturing_bom',
    'production_order',
    'quality_inspection',
    'warehouse_picking_list',
  ],
  organizational: ['organizational_state'],
};

const VALID_DOMAINS: BusinessDomain[] = [
  'internal_entity',
  'external_entity',
  'user_entity',
  'sellable_item',
  'service_item',
  'asset_item',
  'consumable_item',
  'raw_material_item',
  'finance_state',
  'finance_flow',
  'finance_budget',
  'legal_entity',
  'currency_rate',
  'intercompany',
  'physical_space',
  'virtual_space',
  'knowledge_report',
  'knowledge_policy',
  'knowledge_plan',
  'semantic_formation',
  'ephemeral_formation',
  'recurrent_performance',
  'ephemeral_performance',
  'calendar_anchor',
  'calendar_trigger',
  'calendar_cycle',
  'flow_definition',
  'stream_definition',
  'action_binding',
  'flow_execution',
  'stream_execution',
  'flow_event',
  'stream_event',
  'manufacturing_bom',
  'production_order',
  'quality_inspection',
  'warehouse_picking_list',
  'inventory_shortage',
  'organizational_state',
];

const VALID_CATEGORIES: DomainCategory[] = [
  'entity',
  'item',
  'finance',
  'space',
  'knowledge',
  'appeal',
  'calendar',
  'event',
  'manufacturing',
  'finance_advanced',
  'organizational',
];

export function getDomainCategory(businessDomain: BusinessDomain): DomainCategory {
  return DOMAIN_MAP[businessDomain];
}

export function getBusinessDomainsByCategory(domainCategory: DomainCategory): BusinessDomain[] {
  return CATEGORY_MAP[domainCategory];
}

export function isBusinessDomain(value: string): value is BusinessDomain {
  return VALID_DOMAINS.includes(value as BusinessDomain);
}

export function isDomainCategory(value: string): value is DomainCategory {
  return VALID_CATEGORIES.includes(value as DomainCategory);
}

export function createPermissionString(
  domain: BusinessDomain,
  action: 'create' | 'read' | 'update' | 'delete'
): string {
  return `${domain}:${action}`;
}

const LEGACY_TO_STANDARD: Record<string, BusinessDomain> = {
  'entity:internal': 'internal_entity',
  'entity:external': 'external_entity',
  'entity:user': 'user_entity',
  'item:sellable': 'sellable_item',
  'item:service': 'service_item',
  'item:asset': 'asset_item',
  'item:consumable': 'consumable_item',
  'item:raw-material': 'raw_material_item',
  'finance:state': 'finance_state',
  'finance:flow': 'finance_flow',
  'finance:journal': 'finance_state',
  'space:physical': 'physical_space',
  'space:virtual': 'virtual_space',
  'knowledge:report': 'knowledge_report',
  'knowledge:policy': 'knowledge_policy',
  'knowledge:plan': 'knowledge_plan',
  'appeal:semantic-formation': 'semantic_formation',
  'appeal:ephemeral-formation': 'ephemeral_formation',
  'appeal:recurrent-performance': 'recurrent_performance',
  'appeal:ephemeral-performance': 'ephemeral_performance',
  'calendar:event': 'calendar_anchor',
  'calendar:anchor': 'calendar_anchor',
  'calendar:trigger': 'calendar_trigger',
  'calendar:cycle': 'calendar_cycle',
  'event:flow': 'flow_execution',
  'event:stream': 'stream_execution',
  'marketing:campaign': 'ephemeral_performance',
  'marketing:funnel': 'external_entity',
  'marketing:content-asset': 'semantic_formation',
};

export function convertLegacyPermissionToStandard(legacyPermission: string): string | null {
  if (typeof legacyPermission !== 'string' || legacyPermission.length === 0) {
    return null;
  }
  if (legacyPermission.includes('_')) {
    const [domain, action] = legacyPermission.split(':');
    if (domain && action && isBusinessDomain(domain)) return legacyPermission;
  }
  const parts = legacyPermission.split(':');
  if (parts.length === 3) {
    const [category, domain, action] = parts;
    const legacyPrefix = `${category}:${domain}`;
    const standardDomain = LEGACY_TO_STANDARD[legacyPrefix];
    if (standardDomain) return `${standardDomain}:${action}`;
  }
  return null;
}

export function getBusinessDomainDisplayName(businessDomain: BusinessDomain): string {
  const displayNames: Record<BusinessDomain, string> = {
    internal_entity: '내부 조직',
    external_entity: '외부 조직',
    user_entity: '사용자',
    sellable_item: '판매 가능 항목',
    service_item: '서비스 항목',
    asset_item: '자산 상품',
    consumable_item: '소모품',
    raw_material_item: '원자재',
    finance_state: '재무 상태',
    finance_flow: '재무 흐름',
    finance_budget: '예산',
    legal_entity: '법인',
    currency_rate: '환율',
    intercompany: '내부거래',
    physical_space: '물리적 공간',
    virtual_space: '가상 공간',
    knowledge_report: '보고서',
    knowledge_policy: '정책',
    knowledge_plan: '계획',
    semantic_formation: '의미적 형성',
    ephemeral_formation: '일시적 형성',
    recurrent_performance: '반복 공연',
    ephemeral_performance: '일시적 공연',
    calendar_anchor: '캘린더 앵커',
    calendar_trigger: '캘린더 트리거',
    calendar_cycle: '캘린더 리듬',
    flow_definition: '플로우 정의',
    stream_definition: '스트림 정의',
    action_binding: 'Action 연결',
    flow_execution: '플로우 실행',
    stream_execution: '스트림 실행',
    flow_event: '플로우 이벤트',
    stream_event: '스트림 이벤트',
    manufacturing_bom: '자재명세서(BOM)',
    production_order: '생산 지시',
    quality_inspection: '품질 검수',
    warehouse_picking_list: '피킹 목록',
    inventory_shortage: '재고 부족',
    organizational_state: '조직 상태',
  };
  return displayNames[businessDomain];
}

export function getDomainCategoryDisplayName(domainCategory: DomainCategory): string {
  const displayNames: Record<DomainCategory, string> = {
    entity: '조직',
    item: '상품',
    finance: '재무',
    space: '공간',
    knowledge: '지식',
    appeal: '대외 표현·캠페인',
    calendar: '캘린더',
    event: '로그',
    manufacturing: '제조',
    finance_advanced: '고급 재무',
    organizational: '조직 상태',
  };
  return displayNames[domainCategory];
}

export function getAllBusinessDomains(): BusinessDomain[] {
  return [...VALID_DOMAINS];
}

export function getAllDomainCategories(): DomainCategory[] {
  return [...VALID_CATEGORIES];
}

const TABLE_FORMAT_MAP: Record<BusinessDomain, string> = {
  internal_entity: 'entity:internal',
  external_entity: 'entity:external',
  user_entity: 'entity:user',
  sellable_item: 'item:sellable',
  service_item: 'item:service',
  asset_item: 'item:asset',
  consumable_item: 'item:consumable',
  raw_material_item: 'item:raw-material',
  finance_state: 'finance:state',
  finance_flow: 'finance:flow',
  finance_budget: 'finance:budget',
  legal_entity: 'finance-advanced:legal-entity',
  currency_rate: 'finance-advanced:currency-rate',
  intercompany: 'finance-advanced:intercompany',
  physical_space: 'space:physical',
  virtual_space: 'space:virtual',
  knowledge_report: 'knowledge:report',
  knowledge_policy: 'knowledge:policy',
  knowledge_plan: 'knowledge:plan',
  semantic_formation: 'appeal:semantic-formation',
  ephemeral_formation: 'appeal:ephemeral-formation',
  recurrent_performance: 'appeal:recurrent-performance',
  ephemeral_performance: 'appeal:ephemeral-performance',
  calendar_anchor: 'calendar:anchor',
  calendar_trigger: 'calendar:trigger',
  calendar_cycle: 'calendar:cycle',
  flow_definition: 'event:flow:definition',
  stream_definition: 'event:stream:definition',
  action_binding: 'event:action-binding',
  flow_execution: 'event:flow:execution',
  stream_execution: 'event:stream:execution',
  flow_event: 'event:flow:log',
  stream_event: 'event:stream:log',
  manufacturing_bom: 'manufacturing:bom',
  production_order: 'manufacturing:production-order',
  quality_inspection: 'manufacturing:quality-inspection',
  warehouse_picking_list: 'warehouse:picking-list',
  inventory_shortage: 'inventory:shortage',
  organizational_state: 'organizational:state',
};

const TABLE_TO_DOMAIN: Record<string, BusinessDomain> = {
  'entity:internal': 'internal_entity',
  'entity:external': 'external_entity',
  'entity:user': 'user_entity',
  'item:sellable': 'sellable_item',
  'item:service': 'service_item',
  'item:asset': 'asset_item',
  'item:consumable': 'consumable_item',
  'item:raw-material': 'raw_material_item',
  'finance:state': 'finance_state',
  'finance:flow': 'finance_flow',
  'finance:journal': 'finance_state',
  'space:physical': 'physical_space',
  'space:virtual': 'virtual_space',
  'knowledge:report': 'knowledge_report',
  'knowledge:policy': 'knowledge_policy',
  'knowledge:plan': 'knowledge_plan',
  'appeal:semantic-formation': 'semantic_formation',
  'appeal:ephemeral-formation': 'ephemeral_formation',
  'appeal:recurrent-performance': 'recurrent_performance',
  'appeal:ephemeral-performance': 'ephemeral_performance',
  'calendar:event': 'calendar_anchor',
  'calendar:anchor': 'calendar_anchor',
  'calendar:trigger': 'calendar_trigger',
  'calendar:cycle': 'calendar_cycle',
  'event:flow:definition': 'flow_definition',
  'event:stream:definition': 'stream_definition',
  'event:action-binding': 'action_binding',
  'event:flow:execution': 'flow_execution',
  'event:stream:execution': 'stream_execution',
  'system:admin': 'internal_entity',
  'system:config': 'internal_entity',
  'system:audit': 'internal_entity',
  'manufacturing:bom': 'manufacturing_bom',
  'manufacturing:production-order': 'production_order',
  'manufacturing:quality-inspection': 'quality_inspection',
  'warehouse:picking-list': 'warehouse_picking_list',
  'inventory:shortage': 'inventory_shortage',
  'organizational:state': 'organizational_state',
  'finance-advanced:legal-entity': 'legal_entity',
  'finance-advanced:currency-rate': 'currency_rate',
  'finance-advanced:intercompany': 'intercompany',
  'marketing:campaign': 'ephemeral_performance',
  'marketing:funnel': 'external_entity',
  'marketing:content-asset': 'semantic_formation',
};

export function formatBusinessDomainForTable(domain: BusinessDomain): string {
  return TABLE_FORMAT_MAP[domain] ?? domain;
}

export function parseBusinessDomainFromTable(tableFormat: string): BusinessDomain | null {
  if (TABLE_TO_DOMAIN[tableFormat]) return TABLE_TO_DOMAIN[tableFormat];
  if (isBusinessDomain(tableFormat)) return tableFormat;
  return null;
}

/** BusinessDomain → DomainCategory (SSOT alias for category maps) */
export const BUSINESS_DOMAIN_CATEGORIES: Record<BusinessDomain, DomainCategory> = DOMAIN_MAP;

/** Event category L0 Definition vs Execution subdomain groupings */
export const DOMAIN_CATEGORY_SUBDOMAINS: Record<DomainCategory, readonly string[]> = {
  entity: ['internal_entity', 'external_entity', 'user_entity'],
  item: [
    'sellable_item',
    'service_item',
    'asset_item',
    'consumable_item',
    'raw_material_item',
    'inventory_shortage',
  ],
  finance: ['finance_state', 'finance_flow', 'finance_budget'],
  finance_advanced: ['legal_entity', 'currency_rate', 'intercompany'],
  space: ['physical_space', 'virtual_space'],
  knowledge: ['knowledge_report', 'knowledge_policy', 'knowledge_plan'],
  appeal: [
    'semantic_formation',
    'ephemeral_formation',
    'recurrent_performance',
    'ephemeral_performance',
  ],
  calendar: ['calendar_anchor', 'calendar_trigger', 'calendar_cycle'],
  event: [
    'flow_definition',
    'stream_definition',
    'action_binding',
    'flow_execution',
    'stream_execution',
    'flow_event',
    'stream_event',
  ],
  manufacturing: [
    'manufacturing_bom',
    'production_order',
    'quality_inspection',
    'warehouse_picking_list',
  ],
  organizational: ['organizational_state'],
};

export function getBusinessDomainStats(): {
  totalBusinessDomains: number;
  totalDomainCategories: number;
  businessDomainsByCategory: Record<DomainCategory, number>;
} {
  const allDomainCategories = getAllDomainCategories();
  const businessDomainsByCategory = allDomainCategories.reduce(
    (acc, category) => {
      acc[category] = getBusinessDomainsByCategory(category).length;
      return acc;
    },
    {} as Record<DomainCategory, number>
  );
  return {
    totalBusinessDomains: VALID_DOMAINS.length,
    totalDomainCategories: VALID_CATEGORIES.length,
    businessDomainsByCategory,
  };
}
